import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { DatatableColumn, DatatableSorting, FetchData, RktTableComponent } from '@rocketfinancialcorp/rocket-ui/table';
import { FilterMenuComponent } from '@shared/components';
import { CreateTransactionAdjustmentComponent } from '@shared/components/transactions/create-transaction-adjustment/create-transaction-adjustment.component';
import { FilterField, FilterValues, FinancialAccountDetails, RequestPageParams } from '@shared/models';
import { FinancialAcccountActions, financialAccountFeature } from '@store';
import { compact, identity, isUndefined, pickBy } from 'lodash-es';

@Component({
  selector: 'app-financial-account-activity',
  standalone: true,
  imports: [AsyncPipe, RktIconComponent, RktButtonDirective, FilterMenuComponent, RktTableComponent],
  templateUrl: './financial-account-activity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialAccountActivityComponent {
  store = inject(Store);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  modalService = inject(ModalService);

  financialAccountId = input<string>();
  transactionId = input<string>();

  columns: DatatableColumn[] = compact([
    this.financialAccountId()
      ? { name: 'Txn ID & Created Time', prop: 'idWithCreatedAt', fixedSize: 200, colType: 'id-date-time-combined' }
      : undefined,
    { name: 'Description', prop: 'description', colType: 'two-line-col', flexSize: 450 },
    { name: 'Entry Type', prop: 'entryType', flexSize: 200, colAlign: 'right' },
    { name: 'Amount', prop: 'amount', flexSize: 200, colType: 'amount', colAlign: 'right' },
    { name: 'Account Balance', prop: 'accountBalance', flexSize: 200, colType: 'amount', colAlign: 'right' },
    { name: 'Status', prop: 'status', fixedSize: 140, colType: 'status-tag', colAlign: 'right' },
  ]);

  filters: FilterField[] = compact([
    this.financialAccountId()
      ? {
          name: 'transactionId',
          displayName: 'Transaction Id',
          type: 'INPUT',
        }
      : undefined,
    {
      name: 'date',
      displayName: 'Date',
      type: 'DATE',
      options: [
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: '90 days', value: 'DEFAULT' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      defaultValue: 'DEFAULT',
      props: {
        maxMonths: '15',
      },
    },
    {
      name: 'entryType',
      displayName: 'Amount Type',
      type: 'RADIO',
      options: [
        { label: 'Credits', value: 'CREDIT' },
        { label: 'Debits', value: 'DEBIT' },
        { label: 'OPENING BALANCE', value: 'OPENING_BALANCE' },
      ],
    },
    {
      name: 'amount',
      displayName: 'Amount',
      type: 'AMOUNT',
    },
    {
      name: 'transactionStatus',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Cleared', value: 'CLEARED' },
        { label: 'Declined', value: 'DECLINED' },
        { label: 'Error', value: 'ERROR' },
        { label: 'New', value: 'NEW' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Settled', value: 'SETTLED' },
        { label: 'Reversed', value: 'REVERSED' },
      ],
    },
  ]);

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParams?.page;
    return routePage ? parseInt(routePage, 10) : 0;
  }

  financialAccount$ = this.store.select(financialAccountFeature.selectFinancialAccountDetails);

  financialAccountActivity$ = this.store.select(financialAccountFeature.selectFinancialAccountActivity);

  totalElements$ = this.store.select(financialAccountFeature.selectFinancialAccountActivityTotalElements);

  isLoading$ = this.store.select(financialAccountFeature.selectFinancialAccountActivityLoading);

  activeFilters$ = this.store.select(financialAccountFeature.selectFinancialAccountActivityActiveFilters);

  activeFiltersCount$ = this.store.select(financialAccountFeature.selectFinancialAccountActivityActiveFiltersCount);

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  getFinancialAccountActivity({ page, size, sortParams }: FetchData): void {
    if (sortParams) {
      this.sortParams = sortParams;
    }

    this.updateQueryParams(page);

    this.store.dispatch(
      FinancialAcccountActions.loadFinancialAccountActivity({
        params: {
          page: page ?? 0,
          size,
          sortParams: sortParams ?? this.sortParams,
        },
      }),
    );
  }

  applyFilters(filterValues: FilterValues): void {
    this.store.dispatch(
      FinancialAcccountActions.setFinancialAccountsActivityActiveFilters({ filters: { ...pickBy(filterValues, identity) } }),
    );
    this.getFinancialAccountActivity({ page: 0 });
  }

  createAdjustment(financialAccount: FinancialAccountDetails | null) {
    const modalRef = this.modalService.open(CreateTransactionAdjustmentComponent, {
      className: 'entity-form-modal',
    });

    modalRef.componentInstance.onModalInit(financialAccount);

    modalRef.result.then((result) => {
      if (result) {
        this.getFinancialAccountActivity({ page: 0 });
      }
    });
  }

  onActivityRowClick(transactionId?: string): void {
    if (!transactionId) return;

    this.router.navigateByUrl(`/app/transactions/${transactionId}/details`);
  }
}
